import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Descriptions,
  Divider,
  Form,
  Input,
  Table,
  Tag,
  Typography,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { getBusinessThunk, selectCurrentBusiness } from "../../../businessesSlice";
import {
  IRapydEwallet,
  lookupRapydEwalletsFromServer,
  setRapydEwalletsServer,
} from "../../../businessesApi";
import { IRapydTerminalPairing } from "../../../../../interfaces/businessFull.interface";
import { MissingTag } from "../BusinessDetailsModal";

type Feedback = { type: "success" | "error"; text: string };

const EWALLET_PREFIX_RULE = {
  pattern: /^ewallet_/,
  message: 'Must start with "ewallet_"',
};

// The shared axios interceptor rejects with the response body (`{ message,
// code, type }`), so `err` is that body — not an AxiosError.
function resolveErrorText(err: any, fallback: string): string {
  return (
    err?.message || err?.response?.data?.message || err?.code || fallback
  );
}

export default function BusinessRapydEwallets() {
  const dispatch = useAppDispatch();
  const currentBusiness = useAppSelector(selectCurrentBusiness);
  const rapydNew = currentBusiness?.rapydNew;
  const pairings = rapydNew?.pairings ?? [];

  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [saveFeedback, setSaveFeedback] = useState<Feedback | null>(null);

  const [lookupForm] = Form.useForm();
  const [looking, setLooking] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [ewallets, setEwallets] = useState<IRapydEwallet[] | null>(null);

  // Modal mounts once (no destroyOnClose), so clear all per-business state
  // when the selected business changes — otherwise A's wallets/feedback leak into B.
  useEffect(() => {
    form.resetFields();
    form.setFieldsValue({
      merchantEwalletId: rapydNew?.merchantEwalletId,
      settlementEwalletId: rapydNew?.settlementEwalletId,
    });
    lookupForm.resetFields();
    setEwallets(null);
    setLookupError(null);
    setSaveFeedback(null);
  }, [currentBusiness._id]);

  const handleLookup = async (values: { ewalletReferenceId?: string }) => {
    setLooking(true);
    setLookupError(null);
    setEwallets(null);
    try {
      const data = await lookupRapydEwalletsFromServer({
        ewalletReferenceId: values.ewalletReferenceId?.trim() || undefined,
      });
      setEwallets(data);
    } catch (err: any) {
      setLookupError(resolveErrorText(err, "Failed to look up wallets"));
    } finally {
      setLooking(false);
    }
  };

  const handleSave = async (values: {
    merchantEwalletId: string;
    settlementEwalletId: string;
  }) => {
    setSaving(true);
    setSaveFeedback(null);
    try {
      await setRapydEwalletsServer({
        businessId: currentBusiness._id,
        merchantEwalletId: values.merchantEwalletId.trim(),
        settlementEwalletId: values.settlementEwalletId.trim(),
      });
      setSaveFeedback({ type: "success", text: "Ewallet ids saved." });
      dispatch(getBusinessThunk(currentBusiness._id));
    } catch (err: any) {
      setSaveFeedback({
        type: "error",
        text: resolveErrorText(err, "Failed to save ewallet ids"),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: "0 8px" }}>
      <Descriptions
        title="Rapyd Wallets"
        labelStyle={{ opacity: 0.6, width: 160 }}
        column={1}
      >
        <Descriptions.Item label="Merchant Ewallet">
          {rapydNew?.merchantEwalletId || MissingTag}
        </Descriptions.Item>
        <Descriptions.Item label="Settlement Ewallet">
          {rapydNew?.settlementEwalletId || MissingTag}
        </Descriptions.Item>
      </Descriptions>

      <Divider orientation="left" plain style={{ fontSize: 13 }}>
        Terminal Pairings
      </Divider>
      <Table<IRapydTerminalPairing>
        dataSource={pairings}
        rowKey={(p) => `${p.terminalReference}-${p.deviceId}`}
        size="small"
        pagination={false}
        locale={{ emptyText: "No terminal pairings." }}
        columns={[
          { title: "Terminal", dataIndex: "terminalReference" },
          { title: "Device", dataIndex: "deviceId" },
          { title: "Merchant", dataIndex: "merchantId" },
          { title: "Currency", dataIndex: "currency" },
          {
            title: "Paired At",
            dataIndex: "pairedAt",
            render: (v: string) => (v ? new Date(v).toLocaleString() : ""),
          },
        ]}
      />

      <Divider orientation="left" plain style={{ fontSize: 13 }}>
        Look Up Wallets
      </Divider>
      <Form
        form={lookupForm}
        onFinish={handleLookup}
        layout="inline"
        style={{ marginBottom: 12 }}
      >
        <Form.Item name="ewalletReferenceId" label="Reference id">
          <Input
            placeholder="ewallet_reference_id (optional)"
            allowClear
            style={{ width: 260 }}
          />
        </Form.Item>
        <Form.Item>
          <Button htmlType="submit" icon={<SearchOutlined />} loading={looking}>
            Look up
          </Button>
        </Form.Item>
      </Form>
      {lookupError && (
        <Alert
          type="error"
          message={lookupError}
          showIcon
          style={{ marginBottom: 12 }}
        />
      )}
      {ewallets && (
        <Table<IRapydEwallet>
          dataSource={ewallets}
          rowKey="id"
          size="small"
          pagination={false}
          locale={{ emptyText: "No wallets found." }}
          style={{ marginBottom: 12 }}
          columns={[
            {
              title: "Wallet id",
              dataIndex: "id",
              render: (id: string) => (
                <Typography.Text copyable>{id}</Typography.Text>
              ),
            },
            {
              title: "Reference",
              dataIndex: "ewallet_reference_id",
              render: (v: string) => v || "",
            },
            { title: "Type", dataIndex: "type", render: (v: string) => v || "" },
            {
              title: "Status",
              dataIndex: "status",
              render: (v: string) => (v ? <Tag>{v}</Tag> : ""),
            },
          ]}
        />
      )}

      <Divider orientation="left" plain style={{ fontSize: 13 }}>
        Set Ewallet Ids
      </Divider>
      {saveFeedback && (
        <Alert
          type={saveFeedback.type}
          message={saveFeedback.text}
          showIcon
          closable
          onClose={() => setSaveFeedback(null)}
          style={{ marginBottom: 12 }}
        />
      )}
      <Form form={form} onFinish={handleSave} layout="vertical">
        <Form.Item
          name="merchantEwalletId"
          label="Merchant Ewallet Id"
          rules={[
            { required: true, message: "Merchant ewallet id is required" },
            EWALLET_PREFIX_RULE,
          ]}
        >
          <Input placeholder="ewallet_..." />
        </Form.Item>
        <Form.Item
          name="settlementEwalletId"
          label="Settlement Ewallet Id"
          rules={[
            { required: true, message: "Settlement ewallet id is required" },
            EWALLET_PREFIX_RULE,
          ]}
        >
          <Input placeholder="ewallet_..." />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={saving}>
            Save
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
}
